import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useView } from '../context/ViewContext';
import { usePermissions } from '../hooks/usePermissions';
import {
  HomeIcon,
  DocumentTextIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  NewspaperIcon,
  Cog6ToothIcon,
  LockClosedIcon,
  ArrowsRightLeftIcon,
  Bars3Icon,
  XMarkIcon,
  CalendarDaysIcon,
  BuildingStorefrontIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

export default function UserSidebar() {
  const { user, logout } = useAuth();
  const { canAccessMiellerie, permissions } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const { switchView, hasMultipleViews } = useView();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [canManageMiellerie, setCanManageMiellerie] = useState(false);
  const [canReserveMiellerie, setCanReserveMiellerie] = useState(false);
  const mielleriePathActive =
    location.pathname === '/miellerie' || location.pathname === '/gestion-miellerie';
  const [miellerieOpen, setMiellerieOpen] = useState(mielleriePathActive);

  useEffect(() => {
    if (mielleriePathActive) setMiellerieOpen(true);
  }, [mielleriePathActive]);

  useEffect(() => {
    let cancelled = false;

    const checkGestionMiellerie = async () => {
      if (!user) {
        setCanManageMiellerie(false);
        setCanReserveMiellerie(false);
        return;
      }

      const roles = user.roles || [];
      if (roles.includes('super_admin')) {
        if (!cancelled) {
          setCanManageMiellerie(true);
          setCanReserveMiellerie(true);
        }
        return;
      }

      const token = localStorage.getItem('token');
      const year = new Date().getFullYear();
      let souscrit = false;
      try {
        const services = await axios.get(
          `${process.env.REACT_APP_API_URL}/services/my-services`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const statutsOk = ['en_attente_caution', 'actif'];
        souscrit = (services.data || []).some((s) => {
          if (s.typeService !== 'miellerie' || s.organisme !== 'AMAIR') return false;
          if (Number(s.annee) !== year) return false;
          if (!statutsOk.includes(s.status)) return false;
          const adh = s.adhesion;
          return adh && typeof adh === 'object' && adh.status === 'actif';
        });
      } catch {
        souscrit = false;
      }

      let accesGestion = false;
      if (roles.includes('admin') && (user.organismes || []).includes('AMAIR') && canAccessMiellerie()) {
        accesGestion = true;
      } else {
        try {
          const { data } = await axios.get(
            `${process.env.REACT_APP_API_URL}/miellerie/responsables/mon-statut`,
            {
              headers: { Authorization: `Bearer ${token}` },
              params: { annee: year },
            }
          );
          accesGestion = Boolean(data.accesGestion);
        } catch {
          accesGestion = false;
        }
      }

      if (!cancelled) {
        setCanManageMiellerie(accesGestion);
        setCanReserveMiellerie(souscrit);
      }
    };

    checkGestionMiellerie();
    return () => {
      cancelled = true;
    };
  }, [user, permissions]);

  const handleSwitchToAdmin = () => {
    switchView('admin');
    navigate('/admin/dashboard');
  };

  const navigation = [
    { name: 'Espace personnel', href: '/dashboard', icon: HomeIcon, current: location.pathname === '/dashboard' },
    { name: 'Mes adhésions', href: '/mes-adhesions', icon: DocumentTextIcon, current: location.pathname === '/mes-adhesions' },
    { name: 'Nouvelle adhésion', href: '/adhesion', icon: DocumentTextIcon, current: location.pathname.startsWith('/adhesion') },
    {
      name: 'Miellerie AMAIR',
      href: '/miellerie',
      icon: BuildingStorefrontIcon,
      current: location.pathname === '/miellerie',
      children: (() => {
        const items = [];
        if (canReserveMiellerie) {
          items.push({
            name: 'Mes réservations',
            href: '/miellerie',
            current: location.pathname === '/miellerie',
          });
        }
        if (canManageMiellerie) {
          items.push({
            name: 'Gestion miellerie',
            href: '/gestion-miellerie',
            current: location.pathname === '/gestion-miellerie',
          });
        }
        return items.length ? items : undefined;
      })(),
    },
    { name: 'Agenda', href: '/events', icon: CalendarDaysIcon, current: location.pathname.startsWith('/events') || location.pathname.startsWith('/reunions') },
    { name: 'Actualités', href: '/actualites', icon: NewspaperIcon, current: location.pathname.startsWith('/actualites') },
  ];

  const secondaryNavigation = [
    { name: 'Mon profil', href: '/profil', icon: UserCircleIcon },
    { name: 'Paramètres', href: '/parametres', icon: Cog6ToothIcon },
    { name: 'Sécurité', href: '/securite', icon: LockClosedIcon },
  ];

  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  const linkClass = (current) =>
    `flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
      current
        ? 'bg-honey-200 text-honey-900 shadow-sm'
        : 'text-honey-800 hover:bg-honey-200 hover:text-honey-900'
    }`;

  const SidebarContent = () => (
    <div className="flex flex-col h-full bg-gradient-to-b from-honey-50 to-honey-100 w-64 border-r border-honey-200">
      <div className="flex items-center h-16 px-6 bg-honey-100 border-b border-honey-200">
        <div className="flex items-center space-x-4">
          <img src="/icon.png" alt="Logo Apiculture" className="w-auto h-10" />
          <div>
            <div className="text-honey-900 font-semibold text-sm">Abeille Réunion</div>
            <div className="text-honey-700 text-xs">Espace adhérent</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navigation.map((item) => {
          const Icon = item.icon;
          const hasChildren = Array.isArray(item.children) && item.children.length > 0;

          if (!hasChildren) {
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={handleNavClick}
                className={linkClass(item.current)}
              >
                <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
                {item.name}
              </Link>
            );
          }

          const groupActive = item.children.some((c) => c.current) || item.current;

          return (
            <div key={item.name} className="space-y-0.5">
              <button
                type="button"
                onClick={() => setMiellerieOpen((o) => !o)}
                className={`w-full ${linkClass(groupActive && !miellerieOpen)}`}
                aria-expanded={miellerieOpen}
              >
                <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
                <span className="flex-1 text-left">{item.name}</span>
                <ChevronDownIcon
                  className={`h-4 w-4 flex-shrink-0 transition-transform ${
                    miellerieOpen ? 'rotate-180' : ''
                  }`}
                  aria-hidden
                />
              </button>
              {miellerieOpen && (
                <div className="ml-4 pl-3 border-l border-honey-300 space-y-0.5">
                  {item.children.map((child) => (
                    <Link
                      key={child.name}
                      to={child.href}
                      onClick={handleNavClick}
                      className={linkClass(child.current)}
                    >
                      {child.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="px-3 py-4 border-t border-honey-200">
        {secondaryNavigation.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={handleNavClick}
              className="flex items-center px-3 py-2 text-sm font-medium text-honey-800 rounded-md hover:bg-honey-200 hover:text-honey-900 transition-colors"
            >
              <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
              {item.name}
            </Link>
          );
        })}

        <button
          onClick={logout}
          className="w-full flex items-center px-3 py-2 mt-1 text-sm font-medium text-honey-800 rounded-md hover:bg-honey-200 hover:text-honey-900 transition-colors"
        >
          <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5 flex-shrink-0" />
          Déconnexion
        </button>
      </div>

      <div className="px-3 py-4 bg-honey-100 border-t border-honey-200">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-honey-500 rounded-full flex items-center justify-center shadow-sm">
              <span className="text-white text-sm font-medium">
                {user?.prenom?.[0]}
                {user?.nom?.[0]}
              </span>
            </div>
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-honey-900">
              {user?.prenom} {user?.nom}
            </p>
            <p className="text-xs text-honey-700">Adhérent</p>
          </div>
        </div>

        {hasMultipleViews() && (
          <button
            onClick={handleSwitchToAdmin}
            className="w-full flex items-center px-3 py-2 mt-4 mb-2 text-sm font-medium text-white bg-gray-800 rounded-md hover:bg-gray-900 transition-colors shadow-sm"
          >
            <ArrowsRightLeftIcon className="mr-3 h-5 w-5 flex-shrink-0" />
            Espace admin
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
      <button
        onClick={() => setMobileMenuOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-40 p-2 bg-honey-100 rounded-lg shadow-md border border-honey-200"
      >
        <Bars3Icon className="h-6 w-6 text-honey-900" />
      </button>

      <div className="hidden lg:flex lg:flex-shrink-0">
        <SidebarContent />
      </div>

      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setMobileMenuOpen(false)}
          />

          <div className="fixed inset-y-0 left-0 w-64 shadow-xl">
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="absolute top-4 right-4 p-1 text-honey-700 hover:text-honey-900"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
            <SidebarContent />
          </div>
        </div>
      )}
    </>
  );
}
